import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck2,
  CalendarClock,
  Cake,
  Palmtree,
  Sparkles,
} from "lucide-react";
import { format, getISOWeek } from "date-fns";
import { nl } from "date-fns/locale";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState } from "../components/Feedback";
import { MonthCalendar, PlannerLegend } from "../components/MonthCalendar";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  BalanceSummary,
  CalendarDay,
  CalendarResponse,
  LeaveEntry,
  Paginated,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { formatDate, formatMinutes, organizationQuery } from "../utils/format";

type WarningItem = {
  bucket_id: number;
  remaining_minutes: number;
  leave_type: string;
  days_left: number;
  expires_on: string;
};

type ScheduleVersion = {
  id: number;
  label: string;
  total_minutes_per_week: number;
  days: Array<{ weekday: number; minutes: number }>;
};

type LegacySchedule = {
  id: number;
  parity: "all" | "even" | "odd";
  weekday: number;
  hours: string;
};

const weekdayLabels = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [balances, setBalances] = useState<BalanceSummary[]>([]);
  const [warnings, setWarnings] = useState<WarningItem[]>([]);
  const [calendar, setCalendar] = useState<CalendarResponse | null>(null);
  const [entries, setEntries] = useState<LeaveEntry[]>([]);
  const [schedule, setSchedule] = useState<ScheduleVersion | null>(null);
  const [approvalCount, setApprovalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [today] = useState(() => new Date());

  useEffect(() => {
    const query = organizationQuery(organizationId);
    const suffix = query ? `?${query}` : "";
    const join = query ? `&${query}` : "";
    setLoading(true);
    setError("");
    void (async () => {
      try {
        const requests = [
          api.get(`/leave/balances/overview/${suffix}`),
          api.get(`/leave/warnings/${suffix}`),
          api.get<CalendarResponse>(
            `/leave/calendar/overview/?granularity=month&date=${format(
              today,
              "yyyy-MM-dd",
            )}${join}`,
          ),
          api.get<Paginated<LeaveEntry> | LeaveEntry[]>(
            `/leave/entries/?ordering=start_date&${
              organizationId ? `organization=${organizationId}` : "context=personal"
            }`,
          ),
          api.get<Paginated<ScheduleVersion> | ScheduleVersion[]>(
            `/leave/schedule-versions/${suffix}`,
          ),
          api.get<Paginated<LegacySchedule> | LegacySchedule[]>(
            "/leave/work-schedules/",
          ),
        ];
        const [
          balanceResponse,
          warningResponse,
          calendarResponse,
          entryResponse,
          scheduleResponse,
          legacyScheduleResponse,
        ] = await Promise.all(requests);
        setBalances(balanceResponse.data.summary);
        setWarnings(warningResponse.data.warnings);
        setCalendar(calendarResponse.data as CalendarResponse);
        setEntries(
          unwrapResults(entryResponse.data as Paginated<LeaveEntry> | LeaveEntry[]),
        );
        const schedules = unwrapResults(
          scheduleResponse.data as Paginated<ScheduleVersion> | ScheduleVersion[],
        );
        const legacySchedules = unwrapResults(
          legacyScheduleResponse.data as Paginated<LegacySchedule> | LegacySchedule[],
        );
        setSchedule(
          schedules[0] ??
            (!organizationId
              ? legacyScheduleForCurrentWeek(legacySchedules, today)
              : null),
        );
        if (organizationId && capabilities.canApproveLeave) {
          const approvals = await api.get(
            `/organizations/${organizationId}/approvals/`,
          );
          setApprovalCount(approvals.data.count);
        } else {
          setApprovalCount(0);
        }
      } catch {
        setError("Het dashboard kon niet volledig worden geladen.");
      } finally {
        setLoading(false);
      }
    })();
  }, [organizationId, capabilities.canApproveLeave, today]);

  const balanceByCategory = useMemo(
    () => new Map(balances.map((row) => [row.category, row])),
    [balances],
  );
  const primaryBalance = balanceByCategory.get("leave") ?? balances[0];
  const requestedMinutes = balances.reduce(
    (sum, row) => sum + row.requested_minutes,
    0,
  );
  const plannedMinutes = balances.reduce((sum, row) => sum + row.planned_minutes, 0);
  const transportMinutes = balances.reduce(
    (sum, row) => sum + row.transport_minutes,
    0,
  );
  const dayMap = useMemo(
    () => new Map(calendar?.days.map((day) => [day.date, day]) ?? []),
    [calendar],
  );
  const todayIso = format(today, "yyyy-MM-dd");
  const upcomingEntries = entries
    .filter((entry) => entry.end_date >= format(today, "yyyy-MM-dd"))
    .sort((a, b) => a.start_date.localeCompare(b.start_date))
    .slice(0, 4);
  const upcomingCalendarItems = calendar?.days
    .filter((day) => day.date >= format(today, "yyyy-MM-dd"))
    .filter(
      (day) =>
        day.layers.public_holidays?.length ||
        day.layers.events?.length ||
        day.layers.school_holidays?.length,
    )
    .slice(0, 4);

  if (loading) return <LoadingState label="Dashboard laden..." />;

  return (
    <div className="dashboard-page">
      <PageHeader
        eyebrow={organization ? organization.name : "Persoonlijke verlofkaart"}
        title={`Goedemorgen${user?.first_name ? `, ${user.first_name}` : ""}`}
        description="Je saldo, planning en komende vrije dagen in één overzicht."
        actions={
          <Link className="button primary" to="/app/requests/new">
            <CalendarCheck2 size={18} /> Nieuw verlof
          </Link>
        }
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}

      <section className="metric-grid" aria-label="Verlofsamenvatting">
        <article className="metric-card metric-primary">
          <span className="metric-icon">
            <Palmtree size={20} />
          </span>
          <div>
            <span>Huidig verlofsaldo</span>
            <strong>{formatMinutes(primaryBalance?.current_minutes ?? 0)}</strong>
            <small>
              {formatMinutes(primaryBalance?.available_minutes ?? 0)} beschikbaar na
              planning
            </small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-mint">
            <Sparkles size={20} />
          </span>
          <div>
            <span>ATV</span>
            <strong>
              {formatMinutes(
                balanceByCategory.get("atv")?.current_minutes ?? 0,
              )}
            </strong>
            <small>Apart bijgehouden</small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-amber">
            <CalendarClock size={20} />
          </span>
          <div>
            <span>Transport</span>
            <strong>{formatMinutes(transportMinutes)}</strong>
            <small>
              {warnings.length
                ? `${warnings.length} vervalwaarschuwing(en)`
                : "Geen spoed"}
            </small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-violet">
            <CalendarCheck2 size={20} />
          </span>
          <div>
            <span>Gepland / aangevraagd</span>
            <strong>{formatMinutes(plannedMinutes + requestedMinutes)}</strong>
            <small>{formatMinutes(requestedMinutes)} in afwachting</small>
          </div>
        </article>
      </section>

      {approvalCount > 0 && (
        <Link className="approval-callout" to="/app/team?view=approvals">
          <span>
            <ClipboardIcon /> {approvalCount} aanvraag{approvalCount === 1 ? "" : "en"}{" "}
            wachten op fiattering
          </span>
          <ArrowRight size={18} />
        </Link>
      )}

      <div className="dashboard-layout">
        <section className="surface calendar-surface">
          <div className="surface-heading">
            <div>
              <p className="eyebrow">Maandoverzicht</p>
              <h2>{format(today, "MMMM yyyy", { locale: nl })}</h2>
            </div>
            <Link className="text-link" to="/app/planner">
              Open jaarplanner <ArrowRight size={15} />
            </Link>
          </div>
          <MonthCalendar
            month={today}
            dayMap={dayMap}
            selection={null}
            onSelect={(iso) => navigate(`/app/requests/new?date=${iso}`)}
            todayIso={todayIso}
          />
          <PlannerLegend />
        </section>

        <aside className="dashboard-aside">
          <section className="surface compact-surface">
            <div className="surface-heading">
              <h2>Komende vrije dagen</h2>
            </div>
            <div className="timeline-list">
              {upcomingEntries.length === 0 && (
                <p className="empty-copy">Nog niets gepland.</p>
              )}
              {upcomingEntries.map((entry) => (
                <Link key={entry.id} to="/app/requests" className="timeline-item">
                  <span className="date-tile">
                    <strong>{formatDate(entry.start_date, "d")}</strong>
                    <small>{formatDate(entry.start_date, "MMM")}</small>
                  </span>
                  <span>
                    <strong>{entry.leave_type_name}</strong>
                    <small>
                      {formatDate(entry.start_date)} ·{" "}
                      {formatMinutes(entry.requested_minutes)}
                    </small>
                  </span>
                  <StatusBadge status={entry.status} />
                </Link>
              ))}
            </div>
          </section>

          <section className="surface compact-surface">
            <div className="surface-heading">
              <h2>Werkpatroon</h2>
            </div>
            <div className="schedule-list">
              {weekdayLabels.map((label, index) => {
                const minutes =
                  schedule?.days.find((day) => day.weekday === index)?.minutes ?? 0;
                return (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{minutes ? formatMinutes(minutes) : "Vrij"}</strong>
                  </div>
                );
              })}
            </div>
            <div className="schedule-total">
              <span>Totaal per week</span>
              <strong>{formatMinutes(schedule?.total_minutes_per_week ?? 0)}</strong>
            </div>
          </section>

          <section className="surface compact-surface">
            <div className="surface-heading">
              <h2>Kalenderlagen</h2>
            </div>
            <div className="event-list">
              {upcomingCalendarItems?.map((day) => (
                <div key={day.date} className="event-list-item">
                  <span
                    className={`event-icon ${
                      day.layers.public_holidays?.length
                        ? "holiday"
                        : day.layers.events?.length
                        ? "birthday"
                        : "school"
                    }`}
                  >
                    {day.layers.events?.length ? (
                      <Cake size={16} />
                    ) : (
                      <CalendarCheck2 size={16} />
                    )}
                  </span>
                  <span>
                    <strong>
                      {day.layers.public_holidays?.[0]?.name ??
                        day.layers.events?.[0]?.title ??
                        schoolPeriodLabel(day.layers.school_holidays?.[0])}
                    </strong>
                    <small>{formatDate(day.date)}</small>
                  </span>
                </div>
              ))}
              {!upcomingCalendarItems?.length && (
                <p className="empty-copy">Geen items deze maand.</p>
              )}
            </div>
          </section>
        </aside>
      </div>

      {warnings.length > 0 && (
        <section className="notice notice-warning">
          <AlertTriangle size={18} />
          <span>
            {warnings[0].leave_type}: {formatMinutes(warnings[0].remaining_minutes)}{" "}
            vervalt op {formatDate(warnings[0].expires_on)}.
          </span>
        </section>
      )}
    </div>
  );
}

function schoolPeriodLabel(
  period?: NonNullable<CalendarDay["layers"]["school_holidays"]>[number],
) {
  if (!period) return "Schoolvrij";
  const label = period.display_name ?? period.name;
  const suffix =
    period.part === "morning"
      ? " (ochtend)"
      : period.part === "afternoon"
      ? " (middag)"
      : "";
  return period.event_type === "study_day" ? `Studiedag: ${label}${suffix}` : label;
}

function ClipboardIcon() {
  return <CalendarClock size={18} aria-hidden="true" />;
}

function legacyScheduleForCurrentWeek(
  rows: LegacySchedule[],
  referenceDate: Date,
): ScheduleVersion | null {
  if (!rows.length) return null;
  const parity = getISOWeek(referenceDate) % 2 === 0 ? "even" : "odd";
  const days = weekdayLabels.map((_, weekday) => {
    const row =
      rows.find(
        (candidate) => candidate.weekday === weekday && candidate.parity === parity,
      ) ??
      rows.find(
        (candidate) => candidate.weekday === weekday && candidate.parity === "all",
      );
    return { weekday, minutes: Math.round(Number(row?.hours ?? 0) * 60) };
  });
  return {
    id: 0,
    label: "Huidig werkpatroon",
    days,
    total_minutes_per_week: days.reduce((total, day) => total + day.minutes, 0),
  };
}
